import { useState, useEffect } from "react";
import { Database, Cloud, CheckCircle, RefreshCw, Wifi } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SettingsPage = () => {
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('checking');

  useEffect(() => {
    // Auto-test connection on load
    testFirebaseConnection(true);
  }, []);

  const testFirebaseConnection = async (silent = false) => {
    setTestingConnection(true);
    try {
      const { db } = await import('@/lib/firebase');
      const { collection, getDocs } = await import('firebase/firestore');
      
      const testRef = collection(db, 'bizops_test');
      await getDocs(testRef);
      
      setConnectionStatus('connected');
      if (!silent) toast.success('Firebase connection successful!');
    } catch (error) {
      console.error('Firebase connection error:', error);
      setConnectionStatus('error');
      if (!silent) toast.error(`Connection failed: ${error.message}`);
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className="page-container" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
          Settings
        </h1>
        <p className="text-zinc-500 mt-2">Firebase cloud database configuration</p>
      </div>

      {/* Connection Status Card */}
      <Card className="bg-zinc-900/50 border-white/5 max-w-2xl mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Cloud className="text-amber-500" size={20} />
            Firebase Connection
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Your data is synced to Firebase Firestore in real-time
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Display */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${
            connectionStatus === 'connected' 
              ? 'bg-emerald-500/10 border-emerald-500/20' 
              : connectionStatus === 'error'
              ? 'bg-red-500/10 border-red-500/20'
              : 'bg-zinc-800/50 border-white/10'
          }`}>
            <div className="flex items-center gap-3">
              {connectionStatus === 'connected' ? (
                <CheckCircle className="text-emerald-400" size={24} />
              ) : connectionStatus === 'error' ? (
                <Wifi className="text-red-400" size={24} />
              ) : (
                <RefreshCw className="text-zinc-400 animate-spin" size={24} />
              )}
              <div>
                <p className={`font-medium ${
                  connectionStatus === 'connected' ? 'text-emerald-400' 
                  : connectionStatus === 'error' ? 'text-red-400' 
                  : 'text-zinc-400'
                }`}>
                  {connectionStatus === 'connected' ? 'Connected' 
                   : connectionStatus === 'error' ? 'Connection Failed' 
                   : 'Checking...'}
                </p>
                <p className="text-sm text-zinc-500">
                  Project: {process.env.REACT_APP_FIREBASE_PROJECT_ID}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => testFirebaseConnection(false)}
              disabled={testingConnection}
              className="border-white/10 hover:bg-white/5"
            >
              {testingConnection ? (
                <RefreshCw className="animate-spin mr-2" size={14} />
              ) : null}
              Test
            </Button>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-black/20 border border-white/5">
              <CheckCircle className="text-emerald-400" size={16} />
              <span className="text-sm text-zinc-300">Always Synced</span>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-black/20 border border-white/5">
              <CheckCircle className="text-emerald-400" size={16} />
              <span className="text-sm text-zinc-300">Real-time Updates</span>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-black/20 border border-white/5">
              <CheckCircle className="text-emerald-400" size={16} />
              <span className="text-sm text-zinc-300">Access Anywhere</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Collections Info */}
      <Card className="bg-zinc-900/50 border-white/5 max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Database className="text-amber-500" size={20} />
            Firestore Collections
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Your data is organized in these collections
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              { name: 'bizops_events', label: 'Events' },
              { name: 'bizops_inventory', label: 'Inventory' },
              { name: 'bizops_inventory_categories', label: 'Inventory Categories' },
              { name: 'bizops_talent', label: 'Team / Talent' },
              { name: 'bizops_staff', label: 'Staff' },
              { name: 'bizops_roles', label: 'Roles' },
              { name: 'bizops_departments', label: 'Departments' },
              { name: 'bizops_castings', label: 'Castings' },
              { name: 'bizops_invoices', label: 'Invoices' },
              { name: 'bizops_customers', label: 'Customers' },
              { name: 'bizops_models', label: 'Models' },
              { name: 'bizops_tasks', label: 'Tasks' },
              { name: 'bizops_refunds', label: 'Refunds' },
              { name: 'bizops_refund_prices', label: 'Refund Prices' },
              { name: 'bizops_refund_branches', label: 'Refund Branches' },
              { name: 'bizops_refund_banks', label: 'Refund Banks' },
              { name: 'bizops_logistic', label: 'Logistic' },
              { name: 'bizops_expenses', label: 'Expenses' },
              { name: 'bizops_customer_payments', label: 'Customer Payments' },
              { name: 'bizops_staff_line_ids', label: 'Staff LINE IDs' },
            ].map((col) => (
              <div key={col.name} className="p-3 rounded-lg bg-black/20 border border-white/5">
                <p className="text-xs text-zinc-500">{col.label}</p>
                <code className="text-xs text-amber-400">{col.name}</code>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
